/**
 * 购物车推荐产品横向滚动（左右切换）
 * 独立模块：避免 morph 更新购物车后内联 script 不执行导致首页加购后无法切换
 */

/** 重算稳定后再补一次的延迟：图片/字体加载完尺寸才准 */
const SETTLE_DELAY_MS = 300;

/**
 * 已接入滚动/尺寸监听的横滑容器。
 * morph 常常保留同一个 wrapper 元素，不判重就会每轮重建一遍 ResizeObserver。
 * 元素被整段替换时旧的连同监听器一起被回收，不需要额外 teardown。
 * @type {WeakSet<Element>}
 */
const boundScrollWrappers = new WeakSet();

/** 合并到下一帧的重算 */
let refreshFrame = 0;
/** 尺寸稳定后的补算 */
let refreshSettleTimer = 0;

function getMinItemsForControls(block) {
  const min = parseInt(block?.dataset.minItemsForScroll, 10);
  return Number.isFinite(min) && min > 0 ? min : 3;
}

function getScrollStep(wrapper) {
  const firstItem = wrapper.querySelector('.cart-related-product-item');
  if (!firstItem) return 188;
  const gap = parseFloat(getComputedStyle(wrapper).gap) || 8;
  return firstItem.offsetWidth + gap;
}

function updateCartRelatedScroll(block) {
  const wrapper = block.querySelector('[data-cart-related-scroll-wrapper]');
  const controls = block.querySelector('[data-cart-related-scroll-controls]');
  const prevBtn = block.querySelector('[data-cart-related-scroll="prev"]');
  const nextBtn = block.querySelector('[data-cart-related-scroll="next"]');
  const itemCount = block.querySelectorAll('.cart-related-product-item').length;

  if (!wrapper || !controls) return;

  if (itemCount < getMinItemsForControls(block)) {
    controls.classList.add('is-hidden');
    return;
  }

  controls.classList.remove('is-hidden');

  const maxScroll = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth);
  const canScroll = maxScroll > 2;

  if (prevBtn) prevBtn.disabled = !canScroll || wrapper.scrollLeft <= 1;
  if (nextBtn) nextBtn.disabled = !canScroll || wrapper.scrollLeft >= maxScroll - 1;
}

function bindCartRelatedBlock(block) {
  const wrapper = block.querySelector('[data-cart-related-scroll-wrapper]');
  if (!wrapper || boundScrollWrappers.has(wrapper)) return;

  boundScrollWrappers.add(wrapper);

  const refresh = () => updateCartRelatedScroll(block);

  wrapper.addEventListener('scroll', () => requestAnimationFrame(refresh), { passive: true });

  if (typeof ResizeObserver !== 'undefined') {
    // 观察者由被观察元素持有，元素还活着就不会被回收
    new ResizeObserver(refresh).observe(wrapper);
  }
}

function runCartRelatedRefresh() {
  document.querySelectorAll('.cart-related-products').forEach((block) => {
    bindCartRelatedBlock(block);
    updateCartRelatedScroll(block);
  });
}

/**
 * 重绑购物车推荐位的横滑区并重算左右按钮状态。
 * 注意：它只管推荐位横滑，不刷新购物车本身——需要刷新购物车请用 section 渲染。
 *
 * 一次购物车更新会从 morph、cart:update、抽屉打开等多条路径打进来，
 * 每次重算都要读 scrollWidth/clientWidth 强制同步布局，所以这里按帧合并，
 * 另外补一次延迟重算等图片/字体把尺寸撑稳。
 */
export function rebindCartRecommendedScroll() {
  if (!refreshFrame) {
    refreshFrame = requestAnimationFrame(() => {
      refreshFrame = 0;
      runCartRelatedRefresh();
    });
  }

  clearTimeout(refreshSettleTimer);
  refreshSettleTimer = setTimeout(() => {
    refreshSettleTimer = 0;
    runCartRelatedRefresh();
  }, SETTLE_DELAY_MS);
}

function setupCartRelatedScroll() {
  if (window.__ugCartRelatedScrollSetup) return;
  window.__ugCartRelatedScrollSetup = true;

  document.addEventListener('keydown', (event) => {
    // document 级监听，任何按键都会进来：
    // 焦点元素被 morph 移除后 target 可能不是 Element（.closest 会抛 TypeError）；
    // 按住空格会持续重复触发 card.click()，产生重复 quick-add
    const target = event.target;
    if (!(target instanceof Element) || event.repeat) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const card = target.closest('.cart-related-product-item[data-ug-quick-add]');
    if (!(card instanceof HTMLElement)) return;
    if (target.closest('.cart-related-product-bar, [data-ug-quick-add-ignore]')) return;
    event.preventDefault();
    card.click();
  });

  document.addEventListener(
    'click',
    (event) => {
      // 捕获阶段的 document 级监听，target 未必是 Element（morph 移除焦点元素后可能为 null）
      const target = event.target;
      if (!(target instanceof Element)) return;

      const btn = target.closest('[data-cart-related-scroll]');
      if (!btn) return;

      const block = btn.closest('.cart-related-products');
      const wrapper = block?.querySelector('[data-cart-related-scroll-wrapper]');
      if (!block || !wrapper) return;

      event.preventDefault();
      event.stopPropagation();

      const direction = btn.getAttribute('data-cart-related-scroll');
      const step = getScrollStep(wrapper) * (direction === 'next' ? 1 : -1);
      wrapper.scrollBy({ left: step, behavior: 'smooth' });

      requestAnimationFrame(() => updateCartRelatedScroll(block));
    },
    true
  );

  document.addEventListener('cart:update', rebindCartRecommendedScroll);
  // dialog:open 不冒泡，必须用捕获阶段才收得到（捕获总会沿祖先链下行，冒泡才受 bubbles 控制）。
  // 绑 document 而不是抽屉元素，是因为空车→有车时整个 cart-drawer-component 会被 replaceWith。
  document.addEventListener(
    'dialog:open',
    (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('cart-drawer-component')) {
        rebindCartRecommendedScroll();
      }
    },
    true
  );
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      setupCartRelatedScroll();
      rebindCartRecommendedScroll();
    },
    { once: true }
  );
} else {
  setupCartRelatedScroll();
  rebindCartRecommendedScroll();
}
